// src/components/DashboardTasks.jsx (UPDATED - removed Pro restrictions)
import React, { useEffect, useState, useMemo, useLayoutEffect } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { db } from "../firebase/config";
import { useAuth } from "../contexts/AuthContext";
import { PACKAGES } from "../utils/entitlements";
import {
  collection,
  onSnapshot,
  doc,
  updateDoc,
  getDocs,
  addDoc,
  setDoc,
  deleteDoc,
  deleteField,
  getDoc as getFirestoreDoc,
} from "firebase/firestore";
import {
  format,
  addDays,
  subDays,
  isAfter,
  isSameDay,
  startOfDay,
  isBefore,
  differenceInCalendarDays,
} from "date-fns";
import { FaTrashCan } from "react-icons/fa6";
import { FaEdit } from "react-icons/fa";
import { FaStop } from "react-icons/fa6"; // Added for stop recurrence icon
import noti from "../assets/notiSound.mp3";
import { useTranslation } from "react-i18next";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// --- DEFAULT TASK QUOTES (Must match the ones defined in Settings.jsx) ---
const DEFAULT_TASK_QUOTES = {
  0: "Focus on your tasks! Don't let the day run you. 🏃",
  1: "Break it down! Tackle that first task now. 🎯",
  40: "You're on a roll! Keep checking things off. ✅",
  70: "The finish line is near. Momentum is everything! 💨",
  90: "Almost done — clear that list!",
  100: "Mission accomplished! Enjoy your day! 🎉",
};

// ---------------- Component ----------------
export default function DashboardTasks() {
  const { userId, subscriptionTier } = useAuth();
  const isFullOption = subscriptionTier === PACKAGES.PRO; // Keep but remove restrictions
  const { t, i18n } = useTranslation();

  const [isLockedPreview, setIsLockedPreview] = useState(false);
  const [customQuotes, setCustomQuotes] = useState(DEFAULT_TASK_QUOTES);

  const getUserCollection = (collectionName) => {
    if (!userId) return null;
    return collection(db, "users", userId, collectionName);
  };

  const getClientTasksRef = (clientId) => {
    if (!userId || !clientId) return null;
    return collection(db, "users", userId, "clients", clientId, "tasks");
  };

  const getDashboardTaskDoc = (taskId) => {
    if (!userId || !taskId) return null;
    return doc(db, "users", userId, "dashboardTasks", taskId);
  };

  const getClientTaskDoc = (clientId, taskId) => {
    if (!userId || !clientId || !taskId) return null;
    return doc(db, "users", userId, "clients", clientId, "tasks", taskId);
  };

  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [dashboardTasks, setDashboardTasks] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [priority, setPriority] = useState(t("tasks.mediumPriority"));
  const [showPopup, setShowPopup] = useState(false);
  const [newTask, setNewTask] = useState("");
  const [frequency, setFrequency] = useState("sticky");
  const [recurringDays, setRecurringDays] = useState([]);
  const [adding, setAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editTaskData, setEditTaskData] = useState(null);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [showStopRecurrencePopup, setShowStopRecurrencePopup] = useState(false); // New state for stop recurrence confirmation
  const [taskToStop, setTaskToStop] = useState(null); // New state for task to stop

  const itemRefs = React.useRef({});
  const prevRects = React.useRef({});
  const animateNextListChangeRef = React.useRef(null); // 'add' | 'remove' | 'complete'
  const prevListSignatureRef = React.useRef({
    ids: [],
    completedById: {},
    dateKey: null,
  });

  // Use translated week days
  const weekDays = [
    t("days.monday"),
    t("days.tuesday"),
    t("days.wednesday"),
    t("days.thursday"),
    t("days.friday"),
    t("days.saturday"),
    t("days.sunday"),
  ];

  const weekDaysShort = [
    t("days.mondayShort"),
    t("days.tuesdayShort"),
    t("days.wednesdayShort"),
    t("days.thursdayShort"),
    t("days.fridayShort"),
    t("days.saturdayShort"),
    t("days.sundayShort"),
  ];

  const resetPopupState = () => {
    setNewTask("");
    setFrequency("sticky");
    setRecurringDays([]);
    setPriority(t("tasks.mediumPriority"));
    setIsEditing(false);
    setEditTaskData(null);
    setShowPopup(false);
    setIsLockedPreview(false);
  };

  const checkSound = new Audio(noti);

  const handleUpgradeInteraction = (e) => {
    // REMOVED: No longer need to check for Pro subscription
    // All features are now available to all users
  };

  // ---------------- FETCH CUSTOM QUOTES ----------------
  useEffect(() => {
    if (!userId) return;

    const settingsDocRef = doc(db, "users", userId, "settings", "taskTracker");

    const fetchCustomQuotes = async () => {
      try {
        const docSnap = await getFirestoreDoc(settingsDocRef);
        if (docSnap.exists() && docSnap.data().progressQuotes) {
          const fetched = docSnap.data().progressQuotes;
          const safeFetched = Object.fromEntries(
            Object.entries(fetched).map(([key, value]) => [String(key), value])
          );
          setCustomQuotes({ ...DEFAULT_TASK_QUOTES, ...safeFetched });
        } else {
          setCustomQuotes(DEFAULT_TASK_QUOTES);
        }
      } catch (error) {
        console.error("Error fetching custom task quotes:", error);
        setCustomQuotes(DEFAULT_TASK_QUOTES);
      }
    };

    fetchCustomQuotes();
  }, [userId]);

  // Update priority state when language changes
  useEffect(() => {
    if (!priority || priority === "Medium Priority") {
      setPriority(t("tasks.mediumPriority"));
    }
  }, [t, priority]);

  // ---------------- END FETCH CUSTOM QUOTES ----------------

  const mapTaskToFrequency = (task) => {
    if (task.clientId) return "sticky";
    if (task.recurring) {
      if (!task.days || task.days.length === 0) {
        return "daily";
      } else if (task.days.length > 0) {
        return "specific";
      }
    }
    if (!task.date) return "sticky";
    return "sticky";
  };

  const handleEdit = (e, task) => {
    e.stopPropagation();
    setIsEditing(true);
    setEditTaskData(task);
    setNewTask(task.text);

    const taskFrequency = mapTaskToFrequency(task);
    setFrequency(taskFrequency);

    setRecurringDays(task.days || []);
    setPriority(task.priority || t("tasks.mediumPriority"));

    setShowPopup(true);
  };

  // Handle stop recurrence click
  const handleStopRecurrence = (e, task) => {
    e.stopPropagation();
    setTaskToStop(task);
    setShowStopRecurrencePopup(true);
  };

  // Confirm stop recurrence
  const confirmStopRecurrence = async () => {
    if (!taskToStop) return;

    try {
      const taskRef = getDashboardTaskDoc(taskToStop.id);
      if (!taskRef) {
        console.error("Task reference not found");
        return;
      }

      // Update the task to stop recurrence from selected date forward
      await updateDoc(taskRef, {
        recurring: false,
        stopRecurrenceDate: startOfDay(selectedDate), // Store the date when recurrence was stopped
        originalRecurring: taskToStop.recurring, // Keep original settings for reference
        originalDays: taskToStop.days || [],
      });

      // Show success message
      toast.success(t("tasks.recurrenceStopped"), {
        position: "top-right",
        autoClose: 3000,
      });

      // Reset states
      setShowStopRecurrencePopup(false);
      setTaskToStop(null);

      // Update progress for the current day
      await updateDailyProgress(selectedDate);
    } catch (error) {
      console.error("Error stopping recurrence:", error);
      toast.error(t("tasks.stopRecurrenceError"), {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  useEffect(() => {
    const fetchClients = async () => {
      if (!userId) return;
      const snapshot = await getDocs(getUserCollection("clients"));
      const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setClients(data);
    };
    fetchClients();
  }, [userId]);

  useEffect(() => {
    const unsubscribes = [];
    clients.forEach((client) => {
      const tasksRef = getClientTasksRef(client.id);
      if (!tasksRef) return;
      const unsubscribe = onSnapshot(tasksRef, (snap) => {
        const clientTasks = snap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          clientId: client.id,
          recurring: false,
          completedDates: doc.data()?.completedDates || {},
          priority: doc.data()?.priority || t("tasks.notPriority"),
        }));
        setTasks((prev) => {
          const filtered = prev.filter((t) => t.clientId !== client.id);
          return [...filtered, ...clientTasks];
        });
      });
      unsubscribes.push(unsubscribe);
    });
    return () => unsubscribes.forEach((u) => u());
  }, [clients, userId]);

  useEffect(() => {
    if (!userId) return;
    const ref = getUserCollection("dashboardTasks");
    return onSnapshot(ref, (snap) => {
      const data = snap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        completedDates: doc.data()?.completedDates || {},
        days: doc.data()?.days || [],
        recurring: doc.data().recurring ?? false,
        priority: doc.data()?.priority || t("tasks.notPriority"),
        createdAt: toDate(doc.data().createdAt),
        stopRecurrenceDate: doc.data()?.stopRecurrenceDate
          ? toDate(doc.data().stopRecurrenceDate)
          : null,
      }));
      setDashboardTasks(data);
    });
  }, [userId]);

  useEffect(() => {
    const handler = (e) => {
      if (!userId) return;
      const detail = e?.detail;
      if (!detail) return;
      const { clientId, task } = detail;
      if (!clientId || !task) return;

      const clientExists = clients.some((c) => c.id === clientId);
      if (!clientExists) return;

      setTasks((prev) => {
        if (prev.some((t) => t.id === task.id)) return prev;
        return [
          ...prev,
          {
            ...task,
            clientId,
            recurring: task.recurring ?? false,
            completedDates: task.completedDates || {},
            priority: task.priority || t("tasks.notPriority"),
          },
        ];
      });
    };

    window.addEventListener("clientTaskAdded", handler);
    return () => window.removeEventListener("clientTaskAdded", handler);
  }, [clients, userId, t]);

  const tasksWithNames = tasks.map((task) => {
    const client = clients.find((c) => c.id === task.clientId);
    return { ...task, clientName: client?.name || null };
  });

  const dateKey = format(selectedDate, "yyyy-MM-dd");
  const selectedDayStart = startOfDay(selectedDate);
  const selectedDateKey = format(selectedDate, "yyyy-MM-dd");

  const MAX_FUTURE_DAYS = 7;
  const todayStart = startOfDay(new Date());
  const dayDiff = differenceInCalendarDays(selectedDayStart, todayStart);
  const isToday = dayDiff === 0;
  const isFutureView = dayDiff > 0 && dayDiff <= MAX_FUTURE_DAYS;
  const isBeyondMaxFuture = dayDiff > MAX_FUTURE_DAYS;

  const allTasksCombined = useMemo(
    () => [...tasksWithNames, ...dashboardTasks],
    [tasksWithNames, dashboardTasks]
  );

  // Filter tasks to exclude those that have been stopped
  const filteredTasks = allTasksCombined.filter((t) => {
    // Check if recurrence was stopped before or on this date
    if (t.stopRecurrenceDate) {
      const stopDate = startOfDay(toDate(t.stopRecurrenceDate));
      // If recurrence was stopped on or before selected date, don't show it
      if (
        isSameDay(stopDate, selectedDayStart) ||
        isBefore(stopDate, selectedDayStart)
      ) {
        return false;
      }
    }

    const tCreatedDate = toDate(t.createdAt) || new Date(0);

    if (isAfter(startOfDay(tCreatedDate), selectedDayStart)) {
      return false;
    }

    if (t.recurring) {
      const isScheduledForToday =
        !t.days ||
        t.days.length === 0 ||
        t.days.includes(format(selectedDate, "EEEE"));

      return !!isScheduledForToday;
    }

    if (!t.date) {
      if (t.sticky) {
        const createdOK =
          !tCreatedDate || !isAfter(startOfDay(tCreatedDate), selectedDayStart);
        const completedFromKey = t.completedFrom || null;
        if (completedFromKey && selectedDateKey > completedFromKey)
          return false;
        return createdOK;
      }
      return isSameDay(tCreatedDate, selectedDate);
    }

    return isSameDay(toDate(t.date), selectedDate);
  });

  const priorityWeight = (p) => {
    switch ((p || "").toString()) {
      case t("tasks.criticalPriority"):
        return 4;
      case t("tasks.highPriority"):
        return 3;
      case t("tasks.mediumPriority"):
        return 2;
      case t("tasks.notPriority"):
      default:
        return 1;
    }
  };

  const sortedTasks = filteredTasks.sort((a, b) => {
    const aCompleted =
      a.recurring || a.sticky
        ? a.completedDates?.[dateKey] ?? false
        : a.completed;
    const bCompleted =
      b.recurring || b.sticky
        ? b.completedDates?.[dateKey] ?? false
        : b.completed;
    if (aCompleted !== bCompleted) return aCompleted ? 1 : -1;

    const aw = priorityWeight(a.priority);
    const bw = priorityWeight(b.priority);
    if (aw !== bw) return bw - aw;

    const aDate = toDate(a.createdAt) || new Date(0);
    const bDate = toDate(b.createdAt) || new Date(0);
    return bDate - aDate;
  });

  useLayoutEffect(() => {
    const refs = itemRefs.current || {};
    const newRects = {};

    Object.keys(refs).forEach((id) => {
      const el = refs[id];
      if (el && el.getBoundingClientRect) {
        newRects[id] = el.getBoundingClientRect();
      }
    });

    // Only animate list movement after user actions: add, delete, complete/uncomplete.
    // This prevents the "everything moves" effect on day navigation, edits, or snapshots.
    const currentIds = sortedTasks.map((t) => t.id);
    const currentCompletedById = {};

    sortedTasks.forEach((t) => {
      const isDone =
        t.recurring || t.sticky
          ? t.completedDates?.[dateKey] ?? false
          : t.completed;
      currentCompletedById[t.id] = !!isDone;
    });

    const prevSig = prevListSignatureRef.current;
    const sameDateKey = prevSig.dateKey === dateKey;

    const idsChanged =
      sameDateKey &&
      (prevSig.ids.length !== currentIds.length ||
        prevSig.ids.some((id, idx) => id !== currentIds[idx]));

    const completionChanged =
      sameDateKey &&
      Object.keys(currentCompletedById).some(
        (id) => prevSig.completedById?.[id] !== currentCompletedById[id]
      );

    const hasRelevantChange = idsChanged || completionChanged;
    const shouldAnimate =
      !!animateNextListChangeRef.current && hasRelevantChange;

    if (shouldAnimate) {
      Object.keys(newRects).forEach((id) => {
        const prev = prevRects.current[id];
        const curr = newRects[id];
        const el = refs[id];
        if (!prev || !curr || !el) return;
        const deltaY = prev.top - curr.top;
        if (deltaY === 0) return;

        el.style.transition = "none";
        el.style.transform = `translateY(${deltaY}px)`;

        requestAnimationFrame(() => {
          el.style.transition = "transform 500ms cubic-bezier(0.2,0.8,0.2,1)";
          el.style.transform = "";
        });

        const handle = () => {
          el.style.transition = "";
          el.style.transform = "";
          el.removeEventListener("transitionend", handle);
        };
        el.addEventListener("transitionend", handle);
      });

      // Consume the one-shot animation request.
      animateNextListChangeRef.current = null;
    }

    prevRects.current = newRects;
    prevListSignatureRef.current = {
      ids: currentIds,
      completedById: currentCompletedById,
      dateKey,
    };
  }, [sortedTasks, dateKey]);

  const trackableTasks = sortedTasks;
  const total = trackableTasks.length;
  const completed = trackableTasks.filter((t) =>
    t.recurring || t.sticky ? t.completedDates?.[dateKey] ?? false : t.completed
  ).length;

  const progress = total ? Math.round((completed / total) * 100) : 0;

  // Get motivation quote
  const getMotivation = () => {
    const currentLang = i18n.language || "en";
    const isDefaultQuote = (quote) => {
      return Object.values(DEFAULT_TASK_QUOTES).includes(quote);
    };

    let quote;
    if (progress === 0) quote = customQuotes["0"] || DEFAULT_TASK_QUOTES["0"];
    else if (progress < 40)
      quote = customQuotes["1"] || DEFAULT_TASK_QUOTES["1"];
    else if (progress < 70)
      quote = customQuotes["40"] || DEFAULT_TASK_QUOTES["40"];
    else if (progress < 90)
      quote = customQuotes["70"] || DEFAULT_TASK_QUOTES["70"];
    else if (progress < 100)
      quote = customQuotes["90"] || DEFAULT_TASK_QUOTES["90"];
    else quote = customQuotes["100"] || DEFAULT_TASK_QUOTES["100"];

    if (isDefaultQuote(quote)) {
      if (progress === 0) return t("quotes.0");
      if (progress < 40) return t("quotes.1");
      if (progress < 70) return t("quotes.40");
      if (progress < 90) return t("quotes.70");
      if (progress < 100) return t("quotes.90");
      return t("quotes.100");
    } else {
      return quote;
    }
  };

  const handlePrevDay = () => setSelectedDate(subDays(selectedDate, 1));

  const handleNextDay = () => {
    const next = addDays(selectedDate, 1);
    const nextDiff = differenceInCalendarDays(startOfDay(next), todayStart);
    if (nextDiff <= MAX_FUTURE_DAYS) setSelectedDate(next);
  };

  const isSelectedDateInPast = isBefore(startOfDay(selectedDate), todayStart);

  const toggleDay = (day) => {
    setRecurringDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const toggleComplete = async (task) => {
    animateNextListChangeRef.current = "complete";

    const taskRef = task.clientId
      ? getClientTaskDoc(task.clientId, task.id)
      : getDashboardTaskDoc(task.id);

    if (!taskRef) {
      console.error("toggleComplete: missing taskRef (no userId?)");
      return;
    }

    const currentCompleted =
      task.recurring || task.sticky
        ? task.completedDates?.[dateKey] ?? false
        : task.completed;

    if (task.recurring || task.sticky) {
      const newVal = !currentCompleted;

      if (newVal) {
        checkSound.currentTime = 0;
        checkSound.play();
      }

      if (task.sticky) {
        if (newVal) {
          await updateDoc(taskRef, {
            [`completedDates.${dateKey}`]: true,
            completedFrom: dateKey,
          });
        } else {
          await updateDoc(taskRef, {
            [`completedDates.${dateKey}`]: deleteField(),
            ...(task.completedFrom === dateKey
              ? { completedFrom: deleteField() }
              : {}),
          });
        }
      } else {
        await updateDoc(taskRef, {
          [`completedDates.${dateKey}`]: newVal,
        });
      }
    } else {
      await updateDoc(taskRef, { completed: !currentCompleted });
    }

    // Always update progress for TODAY when toggling tasks
    await updateDailyProgress(new Date());
  };

  const updateTask = async () => {
    if (!newTask.trim() || !editTaskData) return;

    setAdding(true);

    const taskRef = editTaskData.clientId
      ? getClientTaskDoc(editTaskData.clientId, editTaskData.id)
      : getDashboardTaskDoc(editTaskData.id);

    if (!taskRef) {
      console.error("updateTask: missing taskRef (no userId?)");
      setAdding(false);
      return;
    }

    let isRecurring = editTaskData.recurring;
    let daysToSave = editTaskData.days;
    let stickyToSave = editTaskData.sticky;

    if (editTaskData.clientId) {
      isRecurring = false;
      daysToSave = [];
    } else {
      // REMOVED: No Pro check - all users can use recurrence features
      if (frequency === "daily") {
        isRecurring = true;
        daysToSave = [];
        stickyToSave = false;
      } else if (frequency === "specific") {
        isRecurring = true;
        daysToSave = recurringDays;
        stickyToSave = false;
      } else {
        isRecurring = false;
        daysToSave = [];
        stickyToSave = true;
      }
    }

    try {
      const updates = {
        text: newTask,
        recurring: isRecurring,
        days: daysToSave,
        priority:
          priority ||
          (editTaskData && editTaskData.priority) ||
          t("tasks.mediumPriority"),
        sticky: stickyToSave,
        // If changing back to recurring, clear stopRecurrenceDate
        ...(isRecurring ? { stopRecurrenceDate: deleteField() } : {}),
      };

      await updateDoc(taskRef, updates);
      resetPopupState();
    } catch (err) {
      console.error("Error updating task:", err);
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = (e, task) => {
    e.stopPropagation();
    setTaskToDelete(task);
    setShowConfirmPopup(true);
  };

  const confirmDelete = async () => {
    if (!taskToDelete) return;

    animateNextListChangeRef.current = "remove";

    try {
      if (taskToDelete.clientId) {
        const taskRef = getClientTaskDoc(
          taskToDelete.clientId,
          taskToDelete.id
        );
        if (taskRef) await deleteDoc(taskRef);
      } else {
        const taskRef = getDashboardTaskDoc(taskToDelete.id);
        if (taskRef) await deleteDoc(taskRef);
      }
      setShowConfirmPopup(false);
      setTaskToDelete(null);
      await updateDailyProgress(selectedDate);
    } catch (error) {
      console.error("Error deleting task:", error);
    }
  };

  const updateDailyProgress = async (date) => {
    const dateKey = format(date, "yyyy-MM-dd");
    const selectedDayStart = startOfDay(date);
    const selectedDateKey = format(date, "yyyy-MM-dd");

    const allTasksForDate = allTasksCombined.filter((t) => {
      // Check if recurrence was stopped before or on this date
      if (t.stopRecurrenceDate) {
        const stopDate = startOfDay(toDate(t.stopRecurrenceDate));
        if (
          isSameDay(stopDate, selectedDayStart) ||
          isBefore(stopDate, selectedDayStart)
        ) {
          return false;
        }
      }

      const tCreatedDate = toDate(t.createdAt) || new Date(0);

      if (isAfter(startOfDay(tCreatedDate), selectedDayStart)) {
        return false;
      }

      if (t.recurring) {
        const isScheduledForToday =
          !t.days ||
          t.days.length === 0 ||
          t.days.includes(format(date, "EEEE"));

        return !!isScheduledForToday;
      } else {
        if (!t.date) {
          if (t.sticky) {
            const createdOK =
              !tCreatedDate ||
              !isAfter(startOfDay(tCreatedDate), selectedDayStart);
            const completedFromKey = t.completedFrom || null;
            if (completedFromKey && selectedDateKey > completedFromKey)
              return false;
            return createdOK;
          }
          return isSameDay(tCreatedDate, date);
        }
        return isSameDay(toDate(t.date), date);
      }
    });

    const totalTasks = allTasksForDate.length;
    const completedTasks = allTasksForDate.filter((t) =>
      t.recurring || t.sticky
        ? t.completedDates?.[selectedDateKey] ?? false
        : t.completed
    ).length;

    const percentage = totalTasks
      ? Math.round((completedTasks / totalTasks) * 100)
      : 0;

    if (!userId) return;
    await setDoc(doc(db, "users", userId, "dashboardProgress", dateKey), {
      date: startOfDay(date),
      percentage,
    });
  };

  const addDashboardTask = async () => {
    if (!newTask.trim() || isSelectedDateInPast) return;

    // REMOVED: Pro subscription check for recurrence features
    // All users can now use daily and specific day recurrence

    animateNextListChangeRef.current = "add";

    setAdding(true);

    let isRecurring = false;
    let daysToSave = [];
    let initialDate = null;
    let sticky = false;

    if (frequency === "daily") {
      isRecurring = true;
      daysToSave = [];
    } else if (frequency === "specific") {
      isRecurring = true;
      daysToSave = recurringDays;
      if (daysToSave.length === 0) {
        alert(t("tasks.selectDayError"));
        setAdding(false);
        return;
      }
    } else if (frequency === "sticky") {
      isRecurring = false;
      sticky = true;
    } else {
      isRecurring = false;
      sticky = true;
    }

    if (isFutureView) {
      initialDate = startOfDay(selectedDate);
      isRecurring = false;
      daysToSave = [];
      sticky = false;
    }

    const priorityToSave = priority || t("tasks.mediumPriority");

    try {
      if (!userId) throw new Error("No user ID available");
      await addDoc(getUserCollection("dashboardTasks"), {
        text: newTask,
        completed: false,
        recurring: isRecurring,
        days: daysToSave,
        date: initialDate,
        sticky: sticky,
        createdAt: new Date(),
        completedDates: {},
        priority: priorityToSave,
        // No stopRecurrenceDate for new tasks
      });
      resetPopupState();
      await updateDailyProgress(selectedDate);
    } catch (err) {
      console.error(err);
    } finally {
      setAdding(false);
    }
  };

  const DaySelector = () => (
    <DaysContainer>
      <CheckboxLabel
        style={{
          width: "100%",
          marginBottom: "0.5rem",
          fontWeight: "bold",
        }}
      >
        {t("tasks.selectDays")}
      </CheckboxLabel>
      <div
        style={{
          display: "flex",
          gap: "0.4rem",
          flexWrap: "wrap",
        }}
      >
        {weekDays.map((day, index) => (
          <DayButton
            key={day}
            active={recurringDays.includes(day)}
            onClick={() => toggleDay(day)}
          >
            {weekDaysShort[index]}
          </DayButton>
        ))}
      </div>
    </DaysContainer>
  );

  // Format date with translation support
  const formatTranslatedDate = (date) => {
    const dayDiff = differenceInCalendarDays(startOfDay(date), todayStart);

    if (dayDiff === 0) return t("tasks.today");
    if (dayDiff === 1) return t("tasks.tomorrow");

    return format(date, "EEEE, MMM d");
  };

  const getAddButtonTitle = () => {
    if (isSelectedDateInPast) return t("tasks.cannotAddPast");
    if (isFutureView)
      return `${t("tasks.addFutureTask")} ${format(
        selectedDate,
        "EEE, MMM d"
      )}`;
    return t("tasks.addTask").replace("+ ", "");
  };

  // Check if task is recurring and not already stopped
  const isRecurringTask = (task) => {
    return task.recurring && !task.stopRecurrenceDate && !task.clientId;
  };

  return (
    <>
      <GlobalStyle />
      <ToastContainer
        position="top-right"
        autoClose={3000}
        newestOnTop={true}
        closeButton={false}
      />

      <Container>
        <Header>
          <Title>{t("tasks.title")}</Title>
          <DateNavigator>
            <NavButton onClick={handlePrevDay}>←</NavButton>
            <strong>{formatTranslatedDate(selectedDate)}</strong>
            <NavButton
              onClick={handleNextDay}
              disabled={dayDiff >= MAX_FUTURE_DAYS}
            >
              →
            </NavButton>
          </DateNavigator>
        </Header>

        {sortedTasks.length > 0 && (
          <>
            <div
              style={{
                textAlign: "center",
                marginBottom: "0.5rem",
                marginTop: "10px",
              }}
            >
              <strong>{progress}%</strong>
            </div>
            <ProgressBarContainer>
              <ProgressBarFill width={progress}>{progress}%</ProgressBarFill>
            </ProgressBarContainer>
            <MotivationText>{getMotivation()}</MotivationText>
          </>
        )}

        {sortedTasks.length === 0 ? (
          <p className="noTasks">{t("tasks.noTasks")}</p>
        ) : (
          <TaskList>
            {sortedTasks.map((task) => {
              const completed =
                task.recurring || task.sticky
                  ? task.completedDates?.[dateKey] ?? false
                  : task.completed;

              const isPushed = false;
              const disablePush = true;
              const showStopIcon = isRecurringTask(task) && !isFutureView; // Only show stop icon for recurring tasks in non-future views

              return (
                <TaskItem
                  key={task.id}
                  ref={(el) => {
                    if (el) itemRefs.current[task.id] = el;
                    else delete itemRefs.current[task.id];
                  }}
                  data-id={task.id}
                  completed={completed}
                  isPushed={isPushed}
                  futureView={isFutureView}
                  onClick={() => {
                    if (isFutureView) return;

                    if (!isAfter(selectedDayStart, startOfDay(new Date()))) {
                      toggleComplete(task);
                    }
                  }}
                >
                  <TaskInfo>
                    {task.clientName && (
                      <ClientName>{task.clientName}:</ClientName>
                    )}
                    <TaskText>{task.text}</TaskText>
                    {isRecurringTask(task) && (
                      <RecurringBadge title={t("tasks.recurringTask")}>
                        🔄
                      </RecurringBadge>
                    )}
                  </TaskInfo>

                  <TaskActions>
                    {!isFutureView && (
                      <>
                        {showStopIcon && (
                          <ActionIcon
                            onClick={(e) => handleStopRecurrence(e, task)}
                            title={t("tasks.stopRecurrence")}
                            className="stop-recurrence-icon"
                          >
                            <FaStop />
                          </ActionIcon>
                        )}
                        <ActionIcon
                          onClick={(e) => handleEdit(e, task)}
                          title={t("tasks.editTask")}
                        >
                          <FaEdit />
                        </ActionIcon>
                        <ActionIcon
                          onClick={(e) => handleDelete(e, task)}
                          title={t("tasks.delete")}
                        >
                          <FaTrashCan />
                        </ActionIcon>
                      </>
                    )}
                  </TaskActions>
                </TaskItem>
              );
            })}
          </TaskList>
        )}
        <AddButton
          onClick={() => {
            resetPopupState();
            setShowPopup(true);
          }}
          disabled={!(isToday || isFutureView)}
          title={getAddButtonTitle()}
        >
          {t("tasks.addTask")}
        </AddButton>
      </Container>

      {/* Add/Edit Task Popup Overlay */}
      {showPopup && (
        <Overlay onClick={resetPopupState}>
          <PopupCard onClick={(e) => e.stopPropagation()}>
            {/* REMOVED: Upgrade overlay since features are now available to all users */}

            <PopupTitle>
              {isEditing ? t("tasks.editTask") : t("tasks.addNewTask")}
            </PopupTitle>
            <Input
              placeholder={t("tasks.taskPlaceholder")}
              value={newTask}
              onChange={(e) => setNewTask(e.target.value)}
            />

            <PrioritySelect
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              aria-label={t("tasks.selectPriority")}
            >
              <option value="">{t("tasks.selectPriority")}</option>
              <option value={t("tasks.notPriority")}>
                {t("tasks.notPriority")}
              </option>
              <option value={t("tasks.mediumPriority")}>
                {t("tasks.mediumPriority")}
              </option>
              <option value={t("tasks.highPriority")}>
                {t("tasks.highPriority")}
              </option>
              <option value={t("tasks.criticalPriority")}>
                {t("tasks.criticalPriority")}
              </option>
            </PrioritySelect>

            {isFutureView && (
              <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 8 }}>
                {t("tasks.futureTaskNote")} {format(selectedDate, "EEE, MMM d")}
                .
              </div>
            )}

            {(!isEditing || (isEditing && !editTaskData.clientId)) && (
              <FrequencySelect
                value={frequency}
                onChange={(e) => {
                  setFrequency(e.target.value);
                  if (e.target.value !== "specific") {
                    setRecurringDays([]);
                  }
                }}
              >
                <option value="sticky">{t("tasks.untilCompleted")}</option>
                <option value="daily">{t("tasks.everyDay")}</option>
                <option value="specific">{t("tasks.specificDays")}</option>
              </FrequencySelect>
            )}

            {frequency === "specific" && DaySelector()}

            <PopupActions>
              <Button variant="cancel" onClick={resetPopupState}>
                {t("tasks.cancel")}
              </Button>
              <Button
                onClick={isEditing ? updateTask : addDashboardTask}
                disabled={
                  adding ||
                  !newTask.trim() ||
                  (frequency === "specific" && recurringDays.length === 0)
                }
              >
                {adding
                  ? t("tasks.saving")
                  : isEditing
                  ? t("tasks.saveChanges")
                  : t("tasks.addTask").replace("+ ", "")}
              </Button>
            </PopupActions>
          </PopupCard>
        </Overlay>
      )}

      {/* Confirmation Popup Overlay for Deletion */}
      {showConfirmPopup && taskToDelete && (
        <Overlay onClick={() => setShowConfirmPopup(false)}>
          <PopupCard onClick={(e) => e.stopPropagation()}>
            <PopupTitle>{t("tasks.confirmDeletion")}</PopupTitle>
            <ConfirmationMessage>
              {t("tasks.deleteConfirmation")}{" "}
              <TaskToConfirm>"{taskToDelete.text}"</TaskToConfirm>?
            </ConfirmationMessage>

            <PopupActions>
              <Button
                variant="cancel"
                onClick={() => setShowConfirmPopup(false)}
              >
                {t("tasks.cancel")}
              </Button>
              <Button variant="danger" onClick={confirmDelete}>
                {t("tasks.delete")}
              </Button>
            </PopupActions>
          </PopupCard>
        </Overlay>
      )}

      {/* Stop Recurrence Confirmation Popup */}
      {showStopRecurrencePopup && taskToStop && (
        <Overlay onClick={() => setShowStopRecurrencePopup(false)}>
          <PopupCard onClick={(e) => e.stopPropagation()}>
            <PopupTitle>{t("tasks.stopRecurrenceTitle")}</PopupTitle>
            <ConfirmationMessage>
              {t("tasks.stopRecurrenceMessage")}{" "}
              <TaskToConfirm>"{taskToStop.text}"</TaskToConfirm>{" "}
              {t("tasks.stopRecurrenceFrom")}{" "}
              <strong>{formatTranslatedDate(selectedDate)}</strong>?
              <div
                style={{
                  marginTop: "10px",
                  fontSize: "0.9em",
                  color: "var(--fontSecondaryColor)",
                }}
              >
                {t("tasks.stopRecurrenceNote")}
              </div>
            </ConfirmationMessage>

            <PopupActions>
              <Button
                variant="cancel"
                onClick={() => setShowStopRecurrencePopup(false)}
              >
                {t("tasks.cancel")}
              </Button>
              <Button variant="warning" onClick={confirmStopRecurrence}>
                {t("tasks.stopRecurrence")}
              </Button>
            </PopupActions>
          </PopupCard>
        </Overlay>
      )}
    </>
  );
}

// Helper function to convert Firestore timestamp or value to Date object
const toDate = (val) => {
  if (!val) return null;
  if (typeof val.toDate === "function") return val.toDate();
  if (val instanceof Date) return val;
  try {
    return new Date(val);
  } catch {
    return null;
  }
};

const GlobalStyle = createGlobalStyle`
  body.popup-open {
    overflow: hidden;
  }
  .Toastify__toast-container {
    padding-top: 20px;
    z-index: 9999;
  }
`;

const Container = styled.div`
  background: var(--secondaryBackgroundColor);
  padding: 2.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  display: flex;
  color: var(--fontPrimaryColor);
  flex-direction: column;
  gap: 0.75rem;
  /* width: 35%; */
  transition: 0.5s;
  position: relative;
  height: stretch;
  max-height: 600px;
  font-family: "Montserrat", sans-serif;
  min-height: 400px;
  @media (max-height: 1100px) {
    max-height: 500px;
  }
  @media (max-width: 950px) {
    /* width: 100%; */
  }
  @media (max-width: 500px) {
    padding: 2em 1.2em;
  }
  .noTasks {
    text-align: center;
    color: var(--fontSecondaryColor);
    margin: 1.5em;
  }
`;

const TaskItem = styled.div`
  background: ${({ isPushed, completed }) => {
    if (isPushed) return "#e0f2fe";
    if (completed) return "var(--completedItem)";
    return "var(--thirdBackgroundColor)";
  }};
  padding: 1rem 0.9rem;
  border-radius: 15px;
  display: flex;
  text-decoration: ${({ completed }) => (completed ? "line-through" : "none")};
  color: ${({ isPushed, completed, futureView }) =>
    isPushed
      ? "#1e40af"
      : completed
      ? "var(--completedItemTxt)"
      : futureView
      ? "#9ca3af"
      : "var(--fontPrimaryColor)"};
  justify-content: space-between;
  align-items: center;
  &:hover {
    background-color: var(--forthBackgroundColor);
  }

  text-decoration: none;

  cursor: ${({ futureView }) => (futureView ? "not-allowed" : "default")};
  opacity: ${({ futureView }) => (futureView ? 0.75 : 1)};
  filter: ${({ futureView }) => (futureView ? "grayscale(10%)" : "none")};
  transition: all 0.5s;

  border-left: 5px solid
    ${({ isPushed, completed }) => {
      if (isPushed) return "#3b82f6";
      if (completed) return "#10b981";
      return "transparent";
    }};

  &:hover {
    background: ${({ isPushed, completed }) => {
      if (isPushed) return "#1c71da";
      if (completed) return "var(--completedItem)";
      return "var(--forthBackgroundColor)";
    }};
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const Title = styled.h2`
  font-size: 1.2rem;
  font-weight: 500;
`;
const DateNavigator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  strong {
    font-weight: 400;
  }
  @media (max-width: 500px) {
    font-size: 0.8rem;
  }
`;
const NavButton = styled.button`
  background: #495269;
  border: none;
  padding: 0.3rem 0.6rem 0.7em 0.6em;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1rem;
  transition: 0.2s;
  color: white;
  &:hover {
    background: #31578a;
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
  @media (max-width: 500px) {
    font-size: 0.8rem;
  }
`;
const ProgressBarContainer = styled.div`
  background: var(--thirdBackgroundColor);
  border-radius: 10px;
  overflow: hidden;
  /* margin-bottom: 0.5rem; */
  height: 25px;
  position: relative;
  transition: 0.5s;
`;
const ProgressBarFill = styled.div`
  height: 100%;
  width: ${({ width }) => width}%;
  transition: width 0.3s ease, background 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: ${({ width }) => (width > 10 ? "flex-end" : "flex-start")};
  padding: 0 0.5rem;
  color: white;
  font-weight: 400;
  transition: 0.5s;
  background: ${({ width }) => {
    if (width < 30) return "#f87171";
    if (width < 70) return "#facc15";
    if (width === 100) return "#23720b";
    return "#22c55e";
  }};
`;
const MotivationText = styled.p`
  text-align: center;
  /* margin-top: 0.25rem; */
  color: var(--fontSecondaryColor);
  font-weight: 400;
  @media (max-width: 500px) {
    font-size: 0.9em;
  }
`;
const TaskInfo = styled.div`
  cursor: pointer;
  flex-grow: 1;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;
const ClientName = styled.span`
  font-weight: 600;
  transition: 0.5s;
`;
const TaskText = styled.span`
  font-size: 0.95rem;
  margin-left: 0.3rem;
  @media (max-width: 500px) {
    font-size: 0.85em;
  }
`;
// Recurring badge
const RecurringBadge = styled.span`
  font-size: 0.8rem;
  opacity: 0.7;
  cursor: help;
`;
const TaskActions = styled.div`
  display: flex;
  gap: 0.8rem;
  margin-left: 1rem;
`;
const ActionIcon = styled.div`
  cursor: pointer;
  color: var(--secondaryFontColor);
  transition: 0.2s;
  &:hover {
    color: ${({ className }) =>
      className === "stop-recurrence-icon" ? "#dc2626" : "#31578a"};
  }
  &.stop-recurrence-icon:hover {
    transform: scale(1.1);
  }
`;
const AddButton = styled.button`
  background: #495269;
  color: white;
  border: none;
  font-family: "Montserrat";
  padding: 1.1em 1.5rem;
  border-radius: 15px;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.9rem;
  transition: 0.2s;
  margin-top: 1em;

  &:hover {
    background: #717d9b;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
    background: #495269;
  }
`;
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  backdrop-filter: blur(10px);
  background: rgba(0, 0, 0, 0.2);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const PopupCard = styled.div`
  background: var(--popupColor);
  padding: 2rem;
  border-radius: 26px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  width: 380px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  animation: fadeIn 0.2s ease;
  position: relative;

  select {
    padding: 15px 20px;
    border-radius: 5px;
    border: none;
    font-family: "Montserrat", sans-serif;
    font-size: 0.9rem;
    background: var(--thirdBackgroundColor);
    outline: none;
    color: var(--fontPrimaryColor);
    cursor: pointer;
    box-shadow: 0 0 10px 1px var(--popupShadow);
    option {
      padding: 15px 20px;
      height: 100px;
    }
  }

  @keyframes fadeIn {
    from {
      transform: scale(0.95);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }
  @media (max-width: 550px) {
    width: 90%;
  }
`;
const PopupTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: var(--fontPrimaryColor);
  text-align: center;
`;
const Input = styled.input`
  padding: 15px 20px;
  border-radius: 5px;
  border: none;
  font-family: "Montserrat", sans-serif;
  font-size: 0.9rem;
  background: var(--thirdBackgroundColor);
  outline: none;
  color: var(--fontPrimaryColor);
  transition: filter 0.3s ease;
  box-shadow: 0 0 10px 1px var(--popupShadow);
  &::placeholder {
    color: var(--fontSecondaryColor);
  }
`;

const FrequencySelect = styled.select`
  padding: 0.6rem;
  border-radius: 8px;
  border: 1px solid #ddd;
  font-size: 0.9rem;
  background-color: var(--thirdBackgroundColor);
  color: var(--fontPrimaryColor);
  transition: 0.2s;
`;

const PrioritySelect = styled.select`
  padding: 0.6rem;
  border-radius: 8px;
  border: 1px solid #ddd;
  font-size: 0.9rem;
  background-color: var(--thirdBackgroundColor);
  color: var(--fontPrimaryColor);
  margin-top: 0.5rem;
  transition: 0.2s;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.85rem;
  cursor: pointer;
`;
const DaysContainer = styled.div`
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
`;
const DayButton = styled.button`
  background: ${({ active }) => (active ? "#111" : "#eee")};
  color: ${({ active }) => (active ? "white" : "#555")};
  border: none;
  padding: 0.4rem 0.7rem;
  border-radius: 6px;
  font-size: 0.8rem;
  cursor: pointer;
  &:hover {
    background: ${({ active }) => (active ? "#333" : "#ddd")};
  }
`;
const PopupActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
`;
const Button = styled.button`
  background: ${({ variant }) => {
    if (variant === "danger") return "#dc2626";
    if (variant === "warning") return "#f59e0b";
    if (variant === "cancel") return "#eee";
    return "#111";
  }};
  color: ${({ variant }) => (variant === "cancel" ? "#111" : "white")};
  border: none;
  padding: 0.6rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.9rem;
  transition: 0.2s;

  &:hover {
    background: ${({ variant }) => {
      if (variant === "danger") return "#b91c1c";
      if (variant === "warning") return "#d97706";
      if (variant === "cancel") return "#ddd";
      return "#333";
    }};
  }
`;
const TaskList = styled.div`
  max-height: 240px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  padding-right: 4px;
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #717d9b;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-track {
    background-color: var(--secondaryBackgroundColor);
  }
`;
const ConfirmationMessage = styled.p`
  font-size: 1rem;
  margin-bottom: 0.5rem;
  color: var(--fontPrimaryColor);
`;
const TaskToConfirm = styled.strong``;

const UpgradeOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(var(--secondaryBackgroundColor), 0.5);
  backdrop-filter: blur(4px);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 2rem;
  z-index: 10;
  color: var(--fontPrimaryColor);
  border: 2px dashed var(--accentColor);
  transition: opacity 0.3s ease;

  h3 {
    color: #f59e0b;
    margin-bottom: 10px;
  }

  p {
    margin-bottom: 1.5rem;
    color: var(--fontSecondaryColor);
  }
`;

const UpgradeButton = styled.a`
  background: #38bdf8;
  color: white;
  padding: 12px 25px;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  transition: 0.2s;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  &:hover {
    background: #0ea5e9;
  }
`;
